import React, { useMemo } from "react";
import dayjs from "dayjs";

// Team-mode palette — cycle of 8 distinct hex colours for up to 8 team members.
// Members beyond 8 wrap back to index 0.
const TEAM_PALETTE = [
  "#00FF99", // neon green
  "#3B82F6", // blue
  "#F59E0B", // amber
  "#8B5CF6", // purple
  "#EC4899", // pink
  "#10B981", // emerald
  "#F97316", // orange
  "#06B6D4", // cyan
];

function teamColor(idx) {
  return TEAM_PALETTE[idx % TEAM_PALETTE.length];
}

// Hex palette per spec.  Status takes priority over leave type.
const STATUS_COLOR = {
  pending:   "#F59E0B",  // amber
  approved:  "#00FF99",  // neon green
  rejected:  "#EF4444",  // red
};
const TYPE_COLOR = {
  annual:    "#00FF99",  // neon green (matches Approved by design)
  sick:      "#3B82F6",  // blue
  casual:    "#8B5CF6",  // purple
  unpaid:    "#F59E0B",
  wfh:       "#3B82F6",
  maternity: "#EC4899",
};
const TYPE_BADGE = {
  annual: "AL", sick: "SL", casual: "CL",
  unpaid: "UL", wfh: "WFH", maternity: "ML",
};

const DEFAULT_STYLE = {
  bg:    "transparent",
  border:"rgba(255,255,255,0.10)",
  text:  "rgba(255,255,255,0.30)",
  badge: "",
};

// Build inline style from a hex base — gives us bg @ 15%, border @ 30%, text @ 100%
function styleFromHex(hex, badge = "") {
  return {
    bg:     `${hex}26`,   // ~15% alpha
    border: `${hex}4D`,   // ~30% alpha
    text:   hex,
    badge,
  };
}

function getLeaveStyle(leave) {
  if (!leave) return DEFAULT_STYLE;
  const status = String(leave.status || "").toLowerCase();
  const type   = String(leave.leave_type || leave.type || "").toLowerCase();
  // Status takes priority — always
  if (status && STATUS_COLOR[status]) {
    return styleFromHex(STATUS_COLOR[status], TYPE_BADGE[type] || "");
  }
  // Fall back to type colour
  if (type && TYPE_COLOR[type]) {
    return styleFromHex(TYPE_COLOR[type], TYPE_BADGE[type] || "");
  }
  return DEFAULT_STYLE;
}

function leaveOnDate(leaveHistory, date) {
  return leaveHistory.find(l => {
    const start = dayjs(l.start_date || l.from);
    const end   = dayjs(l.end_date   || l.to);
    return (date.isAfter(start, "day") || date.isSame(start, "day"))
        && (date.isBefore(end, "day")  || date.isSame(end, "day"));
  });
}

function leavesOnDate(list, date) {
  return list.filter(l => {
    const start = dayjs(l.start_date || l.from);
    const end   = dayjs(l.end_date   || l.to);
    return (date.isAfter(start, "day") || date.isSame(start, "day"))
        && (date.isBefore(end, "day")  || date.isSame(end, "day"));
  });
}

function blackoutOnDate(blackouts, date) {
  return blackouts.find(b => {
    const start = dayjs(b.start_date);
    const end   = dayjs(b.end_date);
    return (date.isAfter(start, "day") || date.isSame(start, "day"))
        && (date.isBefore(end, "day")  || date.isSame(end, "day"));
  });
}

export default function LeaveCalendar({
  calendarMonth, setCalendarMonth,
  leaveHistory = [],
  setShowForm, setFormData,
  loading,
  blackouts = [],
  teamMode = false,
  teamLeaves = [],
  canToggleTeam = false,
  onToggleTeam = null,
}) {
  const today = dayjs();

  // ── Team-mode color assignment (stable per user_id within current member set) ──
  const memberColors = useMemo(() => {
    const seen = new Map();
    teamLeaves.forEach(l => {
      const k = l.user_id || l.user_name;
      if (!k) return;
      if (!seen.has(k)) seen.set(k, seen.size);
    });
    const out = {};
    seen.forEach((idx, k) => { out[k] = teamColor(idx); });
    return out;
  }, [teamLeaves]);

  const days = (() => {
    const result = [];
    const startDay = calendarMonth.startOf("month").day();
    const total    = calendarMonth.daysInMonth();
    for (let i = 0; i < startDay; i++) result.push(null);
    for (let d = 1; d <= total; d++) result.push(calendarMonth.date(d));
    return result;
  })();

  const handleDayClick = (date) => {
    if (!date) return;
    setFormData?.({ leave_type: "", start_date: date.format("YYYY-MM-DD"), end_date: date.format("YYYY-MM-DD"), reason: "", file: null });
    setShowForm?.(true);
  };

  return (
    <div className="rounded-xl border border-[#00ff90]/30 bg-[#0d0d0d] shadow-[0_0_12px_#00ff9022] p-5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <span className="text-xs text-[#00ff90]/60 uppercase tracking-widest font-medium">
            {calendarMonth.format("MMMM YYYY")}
          </span>
          {canToggleTeam && (
            <div className="flex rounded-lg border border-[#00ff90]/20 overflow-hidden">
              <button
                onClick={() => onToggleTeam?.(false)}
                className="px-3 py-1 text-xs transition-colors"
                style={{
                  background: !teamMode ? "rgba(0,255,144,0.15)" : "transparent",
                  color: !teamMode ? "#00FF99" : "rgba(255,255,255,0.4)",
                }}
              >Mine</button>
              <button
                onClick={() => onToggleTeam?.(true)}
                className="px-3 py-1 text-xs transition-colors border-l border-[#00ff90]/20"
                style={{
                  background: teamMode ? "rgba(0,255,144,0.15)" : "transparent",
                  color: teamMode ? "#00FF99" : "rgba(255,255,255,0.4)",
                }}
              >Team</button>
            </div>
          )}
        </div>
        <div className="flex gap-2">
          <button onClick={() => setCalendarMonth(m => m.subtract(1, "month"))}
            className="w-8 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors flex items-center justify-center text-sm">←</button>
          <button onClick={() => setCalendarMonth(dayjs())}
            className="px-3 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors text-xs">Today</button>
          <button onClick={() => setCalendarMonth(m => m.add(1, "month"))}
            className="w-8 h-8 rounded-lg border border-[#00ff90]/20 bg-[#00ff90]/5 text-[#00ff90]/60 hover:text-[#00ff90] hover:border-[#00ff90]/40 transition-colors flex items-center justify-center text-sm">→</button>
        </div>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 gap-1 mb-1">
        {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d => (
          <div key={d} className="text-center text-xs text-[#00ff90]/30 py-1">{d}</div>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 35 }).map((_, i) => (
            <div key={i} className="h-14 rounded-lg bg-white/5 animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-7 gap-1">
          {days.map((date, idx) => {
            if (!date) return <div key={idx} />;
            const isToday  = date.isSame(today, "day");
            const blackout = blackoutOnDate(blackouts, date);

            // ── Team mode: render dots, one per member on leave that day ──
            if (teamMode) {
              const dayLeaves = leavesOnDate(teamLeaves, date);
              const dots = dayLeaves.slice(0, 4);
              return (
                <button
                  key={idx}
                  onClick={() => handleDayClick(date)}
                  title={
                    (blackout ? `Blackout: ${blackout.reason}\n` : "") +
                    (dayLeaves.length
                      ? dayLeaves.map(l => `${l.user_name} (${l.leave_type})`).join("\n")
                      : "No team leave")
                  }
                  className={`h-14 rounded-lg flex flex-col items-center justify-center gap-1 transition-all hover:scale-105 relative ${isToday ? "ring-2 ring-[#00ff90]/60 ring-offset-1 ring-offset-[#0d0d0d]" : ""}`}
                  style={{
                    backgroundColor: blackout ? "rgba(239,68,68,0.08)" : "rgba(255,255,255,0.02)",
                    border: `1px solid ${blackout ? "rgba(239,68,68,0.4)" : "rgba(255,255,255,0.08)"}`,
                    backgroundImage: blackout
                      ? "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(239,68,68,0.08) 4px, rgba(239,68,68,0.08) 6px)"
                      : "none",
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{ color: isToday ? "#00FF99" : "rgba(255,255,255,0.65)" }}>
                    {date.date()}
                  </span>
                  {dots.length > 0 && (
                    <div style={{ display: "flex", gap: 2 }}>
                      {dots.map((l, i) => {
                        const c = memberColors[l.user_id || l.user_name] || teamColor(i);
                        return (
                          <span
                            key={i}
                            style={{
                              width: 5, height: 5, borderRadius: "50%",
                              background: c,
                              boxShadow: `0 0 4px ${c}66`,
                            }}
                          />
                        );
                      })}
                      {dayLeaves.length > 4 && (
                        <span style={{ fontSize: 8, color: "rgba(255,255,255,0.4)", marginLeft: 1 }}>
                          +{dayLeaves.length - 4}
                        </span>
                      )}
                    </div>
                  )}
                </button>
              );
            }

            // ── Personal mode (default) ──
            const leave = leaveOnDate(leaveHistory, date);
            const style = getLeaveStyle(leave);
            // Use a dot indicator (green/amber/red) per the spec for personal mode
            const dotColor = leave
              ? (STATUS_COLOR[String(leave.status || "").toLowerCase()] || style.text)
              : null;
            return (
              <button
                key={idx}
                onClick={() => handleDayClick(date)}
                title={blackout ? `Blackout: ${blackout.reason}` : undefined}
                style={{
                  backgroundColor: blackout ? "rgba(239,68,68,0.08)" : style.bg,
                  borderColor:     blackout ? "rgba(239,68,68,0.4)"  : style.border,
                  borderWidth:     1,
                  borderStyle:     "solid",
                  backgroundImage: blackout
                    ? "repeating-linear-gradient(45deg, transparent, transparent 4px, rgba(239,68,68,0.08) 4px, rgba(239,68,68,0.08) 6px)"
                    : "none",
                }}
                className={`h-14 rounded-lg flex flex-col items-center justify-center gap-0.5 transition-all hover:scale-105 ${isToday ? "ring-2 ring-[#00ff90]/60 ring-offset-1 ring-offset-[#0d0d0d]" : ""}`}>
                <span
                  className="text-sm font-medium"
                  style={{ color: isToday ? "#00FF99" : style.text }}>
                  {date.date()}
                </span>
                {dotColor ? (
                  <span style={{
                    width: 6, height: 6, borderRadius: "50%",
                    background: dotColor, boxShadow: `0 0 4px ${dotColor}66`,
                  }} />
                ) : style.badge ? (
                  <span
                    className="text-[9px] uppercase tracking-wider opacity-70"
                    style={{ color: style.text }}>
                    {style.badge}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      )}

      {/* Legend — status / blackout / team mode */}
      <div className="flex gap-4 flex-wrap mt-4 pt-3 border-t border-white/5">
        {!teamMode && [
          ["Pending",  STATUS_COLOR.pending],
          ["Approved", STATUS_COLOR.approved],
          ["Rejected", STATUS_COLOR.rejected],
        ].map(([label, hex]) => (
          <div key={label} className="flex items-center gap-1.5">
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: hex, boxShadow: `0 0 4px ${hex}66`,
            }} />
            <span className="text-xs text-white/30">{label}</span>
          </div>
        ))}
        {teamMode && (
          <div className="flex items-center gap-1.5">
            <span style={{ display: "inline-flex", gap: 2 }}>
              {TEAM_PALETTE.slice(0, 4).map((c, i) => (
                <span key={i} style={{
                  width: 6, height: 6, borderRadius: "50%",
                  background: c, boxShadow: `0 0 3px ${c}66`,
                }} />
              ))}
            </span>
            <span className="text-xs text-white/30">One dot per team member on leave</span>
          </div>
        )}
        <div className="flex items-center gap-1.5">
          <span
            className="w-3 h-3 rounded-sm border"
            style={{
              backgroundImage: "repeating-linear-gradient(45deg, transparent, transparent 2px, rgba(239,68,68,0.4) 2px, rgba(239,68,68,0.4) 3px)",
              borderColor: "rgba(239,68,68,0.4)",
            }}
          />
          <span className="text-xs text-white/30">Blackout</span>
        </div>
      </div>
    </div>
  );
}